import { useEffect, useRef, useState } from 'react';

interface Props {
  src: string;
  poster: string;
}

/**
 * HeroVideo — autoplay/muted/playsInline with **ping-pong loop**:
 * plays forward to the end, then in reverse to the start, indefinitely.
 *
 * Strategy:
 * 1. First try Chrome's `playbackRate = -1` trick on `timeupdate`.
 * 2. If reverse playback is not supported (some Safari versions), fall
 *    back to a `requestAnimationFrame` loop that manually rewinds
 *    `currentTime`.
 *
 * The element starts at `opacity: 0` and is faded in via `is-ready` once
 * the first frame is decoded. The hero section has the poster image as
 * a CSS background so there's never a black flash during load.
 */
export default function HeroVideo({ src, poster }: Props) {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;

    let rafId: number | null = null;
    let lastTs = 0;
    let manualReverse = false;

    const onTimeUpdate = () => {
      if (!v.duration) return;
      if (manualReverse) return;
      if (v.playbackRate > 0 && v.currentTime >= v.duration - 0.06) {
        try { v.playbackRate = -1; } catch { manualReverse = true; }
      } else if (v.playbackRate < 0 && v.currentTime <= 0.06) {
        try { v.playbackRate = 1; } catch {}
      }
    };

    // rAF fallback for browsers that don't honor negative playbackRate
    const tick = (ts: number) => {
      if (manualReverse && v.duration) {
        if (lastTs === 0) lastTs = ts;
        const dt = (ts - lastTs) / 1000;
        lastTs = ts;
        v.pause();
        v.currentTime = Math.max(0, v.currentTime - dt);
        if (v.currentTime <= 0.05) {
          manualReverse = false;
          lastTs = 0;
          v.playbackRate = 1;
          v.play().catch(() => {});
        }
      } else {
        lastTs = 0;
      }
      rafId = requestAnimationFrame(tick);
    };

    const onEnded = () => {
      try {
        v.playbackRate = -1;
        v.play().catch(() => { manualReverse = true; });
      } catch {
        manualReverse = true;
      }
    };

    const onLoadedData = () => setReady(true);

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('ended', onEnded);
    v.addEventListener('loadeddata', onLoadedData);
    if (v.readyState >= 2) setReady(true);

    v.play().catch(() => { /* autoplay may be blocked — fine */ });
    rafId = requestAnimationFrame(tick);

    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('loadeddata', onLoadedData);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <video
      ref={ref}
      className={
        'absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ' +
        (ready ? 'opacity-100' : 'opacity-0')
      }
      autoPlay muted playsInline preload="auto"
      poster={poster}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}
